import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Btn, Card, Modal, Field, inputCls } from "@/components/cc";
import { ArrowLeft, Lock, Plus, Search, GitMerge, MessageSquarePlus, ExternalLink } from "lucide-react";

const pretty = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const STATUS_COLOR = { open: "text-amber-300", deferred: "text-sky-300", resolved: "text-emerald-300" };
const ACTIONS = [
  ["keep_both", "Keep both accounts"], ["alternative_traditions", "Alternative traditions"],
  ["select_primary", "Select primary editorial record"], ["mark_duplicate", "Mark duplicate"],
  ["merge_references", "Merge references"], ["defer", "Defer"], ["reopen", "Reopen"],
];

export default function CultReconciliation() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [noteModal, setNoteModal] = useState(null);
  const [primaryModal, setPrimaryModal] = useState(null);

  const load = useCallback(() => { api.get("/culture/recon", { params: statusFilter ? { status: statusFilter } : {} }).then((r) => setRows(r.data)).catch(() => {}); }, [statusFilter]);
  useEffect(() => { api.get("/culture/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const doAction = async (c, action, extra = {}) => {
    try { await api.post(`/culture/recon/${c.id}/action`, { action, ...extra }); toast.success(`${pretty(action)} applied`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  if (!meta) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="cult-recon-workspace">
      <button onClick={() => nav("/command-center/culture")} className="text-xs text-zinc-500 hover:text-white flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Culture & Heritage Intelligence</button>
      <PageHeader title="Disputes & Reconciliation" subtitle="Duplicate profiles, endonym vs exonym, conflicting origins, genealogies, succession, translations & provenance. Original accounts, sources and community disagreement are always preserved.">
        <span className="text-xs px-2 py-1 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30 flex items-center gap-1"><Lock className="h-3.5 w-3.5" /> Private</span>
        <Btn data-testid="cult-recon-new" onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> New conflict</Btn>
      </PageHeader>

      <div className="flex items-center gap-2">
        <select data-testid="cult-recon-filter" className={`${inputCls} w-40`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option><option value="open">Open</option><option value="deferred">Deferred</option><option value="resolved">Resolved</option>
        </select>
        <span className="text-[0.65rem] text-zinc-600 ml-auto">{rows.length} conflicts</span>
      </div>

      <div className="space-y-3" data-testid="cult-recon-list">
        {rows.map((c) => (
          <Card key={c.id} data-testid={`cult-recon-${c.id}`} className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-1.5">
                {(c.conflict_types || []).map((t, i) => <span key={`${t}-${i}`} className="text-[0.6rem] px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300">{pretty(t)}</span>)}
              </div>
              <span className={`text-xs uppercase ${STATUS_COLOR[c.status] || "text-zinc-400"}`} data-testid={`cult-recon-status-${c.id}`}>{c.status}{c.resolution ? ` · ${pretty(c.resolution)}` : ""}</span>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {(c.sides || []).map((s) => (
                <div key={s.record_id} className={`border rounded-sm p-2 text-xs ${c.primary_record_id === s.record_id ? "border-emerald-500/50 bg-emerald-500/5" : "border-zinc-800"}`}>
                  <div className="flex items-center justify-between">
                    <button onClick={() => nav(`/command-center/culture/${s.record_id}`)} className="text-sky-300 hover:underline font-medium">{s.title}</button>
                    {c.primary_record_id === s.record_id && <span className="text-[0.55rem] text-emerald-300 uppercase">primary</span>}
                  </div>
                  <div className="text-zinc-500 text-[0.65rem] flex flex-wrap gap-x-2 mt-0.5">
                    <span>{pretty(s.record_type)}</span>{s.cultural_group && <span>· {pretty(s.cultural_group)}</span>}
                    <span>· E: {pretty(s.editorial_verification)}</span><span>· Cm: {pretty(s.community_validation)}</span>
                  </div>
                </div>
              ))}
            </div>
            {c.analyst_note && <div className="text-[0.65rem] text-zinc-500 italic">Why flagged: {c.analyst_note}</div>}
            {(c.context_notes || []).length > 0 && (
              <div className="text-[0.6rem] text-zinc-500 border-l-2 border-zinc-800 pl-2 space-y-0.5" data-testid={`cult-recon-notes-${c.id}`}>
                {c.context_notes.map((n, i) => <div key={i}>• {n.note} <span className="text-zinc-700">({(n.at || "").slice(0, 10)})</span></div>)}
              </div>
            )}
            <div className="text-[0.55rem] text-zinc-600">Created {(c.created_at || "").slice(0, 16).replace("T", " ")}{c.resolved_at ? ` · resolved ${(c.resolved_at || "").slice(0, 16).replace("T", " ")}` : ""}</div>
            <div className="flex flex-wrap gap-1.5 pt-1 border-t border-zinc-900">
              {ACTIONS.map(([a, label]) => (
                <button key={a} data-testid={`cult-recon-action-${a}-${c.id}`}
                  onClick={() => a === "select_primary" ? setPrimaryModal(c) : doAction(c, a)}
                  className="text-[0.6rem] px-2 py-1 rounded-sm border border-zinc-700 text-zinc-300 hover:border-amber-500/50 hover:text-amber-300">{label}</button>
              ))}
              <button data-testid={`cult-recon-action-add_context_note-${c.id}`} onClick={() => setNoteModal({ conflict: c, text: "" })} className="text-[0.6rem] px-2 py-1 rounded-sm border border-zinc-700 text-zinc-300 hover:text-white flex items-center gap-1"><MessageSquarePlus className="h-3 w-3" /> Community-context note</button>
            </div>
            <p className="text-[0.55rem] text-zinc-600">Selecting a primary never erases the secondary account; community disputes are never auto-converted to false.</p>
          </Card>
        ))}
        {rows.length === 0 && <p className="text-sm text-zinc-600">No conflicts.</p>}
      </div>

      {creating && <CreateConflictModal meta={meta} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}

      <Modal open={!!noteModal} onClose={() => setNoteModal(null)} title="Add community-context note">
        {noteModal && (
          <div className="space-y-3">
            <Field label="Context note (preserves community disagreement)"><textarea rows={3} data-testid="cult-recon-note-text" className={inputCls} value={noteModal.text} onChange={(e) => setNoteModal({ ...noteModal, text: e.target.value })} /></Field>
            <Btn data-testid="cult-recon-note-save" onClick={() => { doAction(noteModal.conflict, "add_context_note", { context_note: noteModal.text }); setNoteModal(null); }} className="w-full justify-center">Save note</Btn>
          </div>
        )}
      </Modal>

      <Modal open={!!primaryModal} onClose={() => setPrimaryModal(null)} title="Select primary editorial record">
        {primaryModal && (
          <div className="space-y-2">
            <p className="text-xs text-zinc-500">The other account is preserved as an alternative, not erased.</p>
            {(primaryModal.sides || []).map((s) => (
              <button key={s.record_id} data-testid={`cult-recon-primary-${s.record_id}`} onClick={() => { doAction(primaryModal, "select_primary", { primary_record_id: s.record_id }); setPrimaryModal(null); }} className="w-full text-left border border-zinc-800 rounded-sm p-2 text-xs text-zinc-200 hover:border-emerald-500/50">{s.title} <span className="text-zinc-500">({pretty(s.record_type)})</span></button>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

function CreateConflictModal({ meta, onClose, onCreated }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [types, setTypes] = useState(["duplicate_community"]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { const t = setTimeout(() => api.get("/culture/records", { params: q ? { q } : {} }).then((r) => setOpts(r.data.slice(0, 20))).catch(() => {}), 250); return () => clearTimeout(t); }, [q]);

  const save = async () => {
    if (selected.length < 2) { toast.error("Select at least two records"); return; }
    setSaving(true);
    try { await api.post("/culture/recon", { record_ids: selected.map((s) => s.id), conflict_types: types, note }); toast.success("Conflict created"); onCreated(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal open onClose={onClose} title="New reconciliation conflict" wide>
      <div className="space-y-3" data-testid="cult-recon-create-modal">
        <div className="relative"><Search className="h-4 w-4 absolute left-2 top-2.5 text-zinc-500" /><input className={`${inputCls} pl-8`} placeholder="Search culture records…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <div className="max-h-32 overflow-y-auto border border-zinc-800 rounded-sm divide-y divide-zinc-900">
          {opts.map((o) => <button key={o.id} type="button" onClick={() => !selected.find((s) => s.id === o.id) && setSelected([...selected, o])} className="w-full text-left px-2 py-1 text-xs hover:bg-zinc-900 text-zinc-200">{o.title} <span className="text-zinc-500 text-[0.6rem]">({pretty(o.record_type)})</span></button>)}
        </div>
        <div className="flex flex-wrap gap-1.5">{selected.map((s) => <span key={s.id} className="text-[0.65rem] bg-amber-500/10 border border-amber-500/30 text-amber-200 px-2 py-0.5 rounded-sm flex items-center gap-1">{s.title}<button onClick={() => setSelected(selected.filter((x) => x.id !== s.id))}>×</button></span>)}</div>
        <Field label="Conflict types">
          <div className="flex flex-wrap gap-1.5">{meta.conflict_types.map((t, i) => <button key={`${t}-${i}`} type="button" onClick={() => setTypes(types.includes(t) ? types.filter((x) => x !== t) : [...types, t])} className={`text-[0.6rem] px-2 py-1 rounded-sm border ${types.includes(t) ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "border-zinc-700 text-zinc-400"}`}>{pretty(t)}</button>)}</div>
        </Field>
        <Field label="Why flagged (note)"><textarea rows={2} className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        <Btn data-testid="cult-recon-create-save" onClick={save} disabled={saving} className="w-full justify-center"><GitMerge className="h-4 w-4" /> {saving ? "Creating…" : "Create conflict"}</Btn>
      </div>
    </Modal>
  );
}
